import { useState, useEffect } from 'react';
import { SellerLayout } from '../components/SellerLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getBannedUsers, unbanUser, getPublicProfile, banUser } from '../api/client';
import type { BannedUser } from '../api/client';
import { parseUTC } from '../utils/date';
import { UserAvatar } from '../components/UserAvatar';

export function SellerBannedUsersPage() {
  useDocumentTitle('Banned Users');
  const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Ban-by-username form
  const [searchUsername, setSearchUsername] = useState('');
  const [banReason, setBanReason] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isBanning, setIsBanning] = useState(false);

  useEffect(() => {
    loadBannedUsers();
  }, []);

  const loadBannedUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getBannedUsers();
      setBannedUsers(data.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load banned users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnban = async (userId: string) => {
    setActionLoading(userId);
    try {
      await unbanUser(userId);
      setBannedUsers(prev => prev.filter(u => u.userId !== userId));
    } catch (err) {
      console.error('Failed to unban user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBanByUsername = async () => {
    if (!searchUsername.trim()) return;
    setSearchError(null);
    setIsBanning(true);
    try {
      const profile = await getPublicProfile(searchUsername.trim());
      await banUser(profile.id, banReason.trim() || undefined);
      setSearchUsername('');
      setBanReason('');
      await loadBannedUsers();
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'User not found or ban failed');
    } finally {
      setIsBanning(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = parseUTC(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <SellerLayout>
      <div style={styles.page}>
        <h2 style={styles.pageTitle}>Banned Users</h2>
        <p style={styles.pageDescription}>
          Manage users banned from your shows. Banned users cannot join or participate in any of your live shows.
        </p>

        {/* Ban a user form */}
        <div style={styles.banForm}>
          <h3 style={styles.formTitle}>Ban a User</h3>
          <div style={styles.formRow}>
            <input
              type="text"
              value={searchUsername}
              onChange={e => setSearchUsername(e.target.value)}
              placeholder="Username"
              style={styles.formInput}
            />
            <input
              type="text"
              value={banReason}
              onChange={e => setBanReason(e.target.value)}
              placeholder="Reason (optional)"
              style={{ ...styles.formInput, flex: 2 }}
            />
            <button
              onClick={handleBanByUsername}
              disabled={!searchUsername.trim() || isBanning}
              style={{
                ...styles.banButton,
                opacity: searchUsername.trim() && !isBanning ? 1 : 0.5,
              }}
            >
              {isBanning ? 'Banning...' : 'Ban User'}
            </button>
          </div>
          {searchError && <p style={styles.formError}>{searchError}</p>}
        </div>

        {/* Banned users table */}
        {isLoading ? (
          <div style={styles.loading}>Loading banned users...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : bannedUsers.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={colors.gray[400]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
            </div>
            <p style={styles.emptyTitle}>No banned users</p>
            <p style={styles.emptyText}>Users you ban from your shows will appear here.</p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <div style={styles.tableHeader}>
              <span style={{ ...styles.tableCell, flex: 2 }}>User</span>
              <span style={{ ...styles.tableCell, flex: 2 }}>Reason</span>
              <span style={{ ...styles.tableCell, flex: 1 }}>Date Banned</span>
              <span style={{ ...styles.tableCell, flex: 1, textAlign: 'right' }}>Actions</span>
            </div>
            {bannedUsers.map(banned => (
              <div key={banned.userId} style={styles.tableRow}>
                <div style={{ ...styles.tableCell, flex: 2, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <UserAvatar src={banned.avatarUrl} alt={banned.username || 'User'} size={32} />
                  <div>
                    <span style={styles.username}>{banned.username || 'Unknown'}</span>
                  </div>
                </div>
                <span style={{ ...styles.tableCell, flex: 2, color: colors.gray[600] }}>
                  {banned.reason || '--'}
                </span>
                <span style={{ ...styles.tableCell, flex: 1, color: colors.gray[600] }}>
                  {formatDate(banned.createdAt)}
                </span>
                <div style={{ ...styles.tableCell, flex: 1, textAlign: 'right' }}>
                  <button
                    onClick={() => handleUnban(banned.userId)}
                    disabled={actionLoading === banned.userId}
                    style={styles.unbanButton}
                  >
                    {actionLoading === banned.userId ? 'Unbanning...' : 'Unban'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: '900px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 8px 0',
  },
  pageDescription: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: '0 0 24px 0',
  },
  banForm: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '20px',
    marginBottom: '24px',
  },
  formTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: '0 0 12px 0',
  },
  formRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  formInput: {
    flex: 1,
    padding: '10px 14px',
    fontSize: '14px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    outline: 'none',
  },
  banButton: {
    padding: '10px 20px',
    backgroundColor: colors.status.error,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  formError: {
    fontSize: '13px',
    color: colors.status.error,
    margin: '8px 0 0 0',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: colors.gray[600],
    fontSize: '14px',
  },
  error: {
    padding: '16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    color: colors.status.error,
    fontSize: '14px',
  },
  emptyState: {
    padding: '48px 24px',
    textAlign: 'center',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
  },
  emptyIcon: {
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: '0 0 8px 0',
  },
  emptyText: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderBottom: `1px solid ${colors.gray[300]}`,
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'flex',
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.gray[200]}`,
    alignItems: 'center',
  },
  tableCell: {
    fontSize: '14px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  username: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
    display: 'block',
  },
  displayName: {
    fontSize: '12px',
    color: colors.gray[600],
    display: 'block',
    marginTop: '2px',
  },
  unbanButton: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    color: colors.status.error,
    border: `1px solid ${colors.status.error}`,
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
